import type { LucideIcon } from 'lucide-react';
import { FileText, Send, Clock, CheckCircle2, XCircle } from 'lucide-react';

export const STATUS_COLORS: Record<string, { label: string; className: string; icon: LucideIcon }> = {
  draft: { label: 'Draft', className: 'bg-gray-50 !text-gray-700 border-gray-300', icon: FileText },
  submitted: { label: 'Submitted', className: 'bg-blue-50 !text-blue-700 border-blue-300', icon: Send },
  under_review: { label: 'Under Review', className: 'bg-yellow-50 !text-yellow-700 border-yellow-300', icon: Clock },
  approved: { label: 'Approved', className: 'bg-green-50 !text-green-700 border-green-300', icon: CheckCircle2 },
  rejected: { label: 'Rejected', className: 'bg-red-50 !text-red-700 border-red-300', icon: XCircle },
  closed: { label: 'Closed', className: 'bg-purple-50 !text-purple-700 border-purple-300', icon: CheckCircle2 },
};

export const RISK_COLORS: Record<string, { label: string; className: string }> = {
  low: { label: 'Low', className: 'bg-green-50 !text-green-700 border-green-300' },
  medium: { label: 'Medium', className: 'bg-yellow-50 !text-yellow-700 border-yellow-300' },
  high: { label: 'High', className: 'bg-orange-50 !text-orange-700 border-orange-300' },
  critical: { label: 'Critical', className: 'bg-red-50 !text-red-700 border-red-300' },
};

export const STATUS_TIMELINE = ['draft', 'submitted', 'under_review', 'approved', 'closed'];

/**
 * CR-6510: what the supplier asked for — a Concession (CE) or a Production
 * Permit (PE). Saved on every request, but nothing on screen said which.
 *
 * @param concession - the request (only its type fields are read)
 * @returns "Concession" or "Production Permit"
 */
export function requestTypeLabel(
  concession: { request_type?: string; request_type_display?: string },
): string {
  return concession.request_type_display || requestTypeNoun(concession);
}

/**
 * CR-6510: the plain noun for the request, for wording such as "Reject …"
 * (the API's display label carries the "(PE)" / "(CE)" code).
 *
 * @param concession - the request (only its type is read)
 * @returns "Concession" or "Production Permit"
 */
export function requestTypeNoun(concession: { request_type?: string }): string {
  return concession.request_type === 'production_permit' ? 'Production Permit' : 'Concession';
}

/**
 * CR-6510: a status value as the page labels it, so the history reads
 * "Submitted" → "Under Review" rather than the stored "under_review".
 *
 * @param value - a stored status value
 * @returns its label, or the value itself when it is not a status
 */
export function statusLabel(value: string): string {
  return STATUS_COLORS[value]?.label ?? value;
}
